import { useEffect, useRef } from "react";
import gsap from "gsap";
import * as THREE from "three";
import "./EntryAnimation.scss";

/* ═══════════════════════════════════════════════════════════════
   ENTRY ANIMATION — full-screen chakra + gsap intro
   Extracted from HomeNew.jsx so it can be reused anywhere (e.g. Home.jsx).

   Controlled by env var VITE_SHOW_ENTRY_ANIMATION:
     VITE_SHOW_ENTRY_ANIMATION=true   -> animation plays, then calls onDone()
     VITE_SHOW_ENTRY_ANIMATION=false  -> animation skipped, onDone() called immediately
   ═══════════════════════════════════════════════════════════════ */

const SHOW_ENTRY_ANIMATION = import.meta.env.VITE_SHOW_ENTRY_ANIMATION === "true";

// ---------- THREE.JS BUILDERS ----------

export function buildChakra({ radius = 1.6, spokes = 24, color = 0xb8863d, emissive = 0x3a2609 }) {
  const group = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color, metalness: 0.55, roughness: 0.35, emissive, emissiveIntensity: 0.4 });

  const rim = new THREE.Mesh(new THREE.TorusGeometry(radius, radius * 0.032, 16, 96), mat);
  group.add(rim);

  const hub = new THREE.Mesh(new THREE.SphereGeometry(radius * 0.11, 32, 32), mat);
  group.add(hub);

  for (let i = 0; i < spokes; i++) {
    const angle = (i / spokes) * Math.PI * 2;
    const spoke = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.012, radius * 0.012, radius * 0.97, 8), mat);
    spoke.position.set(Math.cos(angle) * radius * 0.49, Math.sin(angle) * radius * 0.49, 0);
    spoke.rotation.z = angle + Math.PI / 2;
    group.add(spoke);
  }
  return group;
}

function buildParticleField(count = 260, spread = 6) {
  const geo = new THREE.BufferGeometry();
  const pos = new Float32Array(count * 3);
  for (let i = 0; i < count * 3; i++) pos[i] = (Math.random() - 0.5) * spread;
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  const mat = new THREE.PointsMaterial({ color: 0xe8c99b, size: 0.02, transparent: true, opacity: 0.7 });
  return new THREE.Points(geo, mat);
}

// ---------- INTRO OVERLAY (unchanged mechanic) ----------

function ChakraIntro({ onDone }) {
  const canvasWrapRef = useRef(null);
  const overlayRef = useRef(null);
  const wordRef = useRef(null);
  const kickerRef = useRef(null);
  const subRef = useRef(null);

  useEffect(() => {
    const mount = canvasWrapRef.current;
    let raf;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.z = 6;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const point = new THREE.PointLight(0xe8c99b, 2.2, 20);
    point.position.set(3, 3, 4);
    scene.add(point);

    const chakra = buildChakra({});
    chakra.scale.setScalar(0.001);
    scene.add(chakra);

    const particles = buildParticleField();
    scene.add(particles);

    const onResize = () => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", onResize);

    const animate = () => {
      chakra.rotation.z += 0.0032;
      particles.rotation.y += 0.0008;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const word = wordRef.current;
    const letters = word.querySelectorAll("span");

    const tl = gsap.timeline({
      defaults: { ease: "power4.out" },
      onComplete: () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("resize", onResize);
        renderer.dispose();
        if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
        onDone();
      },
    });

    tl.to(chakra.scale, { x: 1, y: 1, z: 1, duration: 1.1, ease: "elastic.out(1,0.65)" }, 0)
      .to(kickerRef.current, { opacity: 1, duration: 0.5 }, 0.2)
      .to(letters, { opacity: 1, y: 0, stagger: 0.035, duration: 0.7 }, 0.35)
      .to(subRef.current, { opacity: 1, duration: 0.5 }, 1.0)
      .to({}, { duration: 0.55 })
      .to(camera.position, { z: 3.2, duration: 0.9, ease: "power2.in" }, "wipe")
      .to([kickerRef.current, word, subRef.current], { opacity: 0, y: -20, duration: 0.5 }, "wipe")
      .to(overlayRef.current, { yPercent: -100, duration: 0.9, ease: "power4.inOut" }, "wipe+=0.25");

    return () => {
      tl.kill();
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const letters = "Raajneeti AI".split("");

  return (
    <div className="rj-intro" ref={overlayRef}>
      <div className="rj-intro-canvas" ref={canvasWrapRef} />
      <div className="rj-intro-copy">
    
        <div className="rj-intro-word" ref={wordRef}>
          {letters.map((ch, i) => (
            <span key={i}>{ch === " " ? "\u00A0" : ch}</span>
          ))}
        </div>
        <div className="rj-intro-sub" ref={subRef}>
          welcome to Raajneeti AI
        </div>
      </div>
    </div>
  );
}

// ---------- PUBLIC WRAPPER ----------
// Decides, based on VITE_SHOW_ENTRY_ANIMATION, whether to actually play the
// chakra intro or just skip straight to onDone(). Use this component,
// not ChakraIntro directly.

export default function EntryAnimation({ onDone }) {
  useEffect(() => {
    if (!SHOW_ENTRY_ANIMATION) {
      onDone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!SHOW_ENTRY_ANIMATION) return null;

  return <ChakraIntro onDone={onDone} />;
}